// SEO helpers — JSON-LD, hreflang, canonical, OG image absolutization

import { SITE_URL, type Locale } from "./i18n";
export { SITE_URL };

export function absUrl(path: string): string {
  if (!path) return SITE_URL;
  if (/^https?:\/\//.test(path)) return path;
  return `${SITE_URL}${path.startsWith("/") ? "" : "/"}${path}`;
}

/** Absolutize an asset URL imported via Vite (e.g. /assets/foo.123.jpg). */
export function absAsset(assetUrl: string): string {
  return absUrl(assetUrl);
}

export interface HreflangOpts {
  /** Path relative to the locale root, always starting with "/". e.g. "/about", "/" */
  path: string;
  /** Current locale, used for canonical */
  locale: Locale;
}

export function hreflangLinks({ path }: HreflangOpts) {
  const cleanPath = path === "/" ? "" : path;
  const daUrl = `${SITE_URL}${cleanPath || "/"}`;
  const enUrl = `${SITE_URL}/en${cleanPath}`;
  return [
    { rel: "alternate", hrefLang: "da", href: daUrl },
    { rel: "alternate", hrefLang: "en", href: enUrl },
    { rel: "alternate", hrefLang: "x-default", href: daUrl },
  ];
}

export function canonicalLink({ path, locale }: HreflangOpts) {
  const cleanPath = path === "/" ? "" : path;
  const url = locale === "da"
    ? `${SITE_URL}${cleanPath || "/"}`
    : `${SITE_URL}/en${cleanPath}`;
  return { rel: "canonical", href: url };
}

/**
 * Combined helper — returns the canonical link, the three hreflang alternates,
 * and the og:locale meta tag for a given (locale, path). Spread the result
 * directly into a route's head() return value.
 */
export function localizedHead({ path, locale }: HreflangOpts) {
  return {
    links: [canonicalLink({ path, locale }), ...hreflangLinks({ path, locale })],
    meta: [
      { property: "og:locale", content: locale === "da" ? "da_DK" : "en_US" },
      {
        property: "og:locale:alternate",
        content: locale === "da" ? "en_US" : "da_DK",
      },
    ],
  };
}

// ----- JSON-LD builders -----

export function organizationJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Farveladeland",
    url: SITE_URL,
    logo: `${SITE_URL}/favicon.ico`,
    sameAs: ["https://www.instagram.com/farveladeland/"],
    address: {
      "@type": "PostalAddress",
      streetAddress: "Munkensvej 4, 2.tv",
      postalCode: "2000",
      addressLocality: "Frederiksberg",
      addressRegion: "Hovedstaden",
      addressCountry: "DK",
    },
    contactPoint: {
      "@type": "ContactPoint",
      email: "butik@farveladeland.com",
      contactType: "customer service",
      areaServed: "Worldwide",
      availableLanguage: ["Danish", "English"],
    },
  };
}

export function localBusinessJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "Store",
    "@id": `${SITE_URL}/#localbusiness`,
    name: "Farveladeland",
    description:
      "Vintage Murano-lamper og kuraterede loppefund. Showroom efter aftale på Frederiksberg.",
    url: SITE_URL,
    image: `${SITE_URL}/favicon.ico`,
    email: "butik@farveladeland.com",
    sameAs: ["https://www.instagram.com/farveladeland/"],
    address: {
      "@type": "PostalAddress",
      streetAddress: "Munkensvej 4, 2.tv",
      postalCode: "2000",
      addressLocality: "Frederiksberg",
      addressCountry: "DK",
    },
    geo: {
      "@type": "GeoCoordinates",
      latitude: 55.6759,
      longitude: 12.5343,
    },
    areaServed: ["DK", "EU", "Worldwide"],
    openingHoursSpecification: [
      {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
        opens: "10:00",
        closes: "17:00",
        description: "Showroom efter aftale",
      },
    ],
    priceRange: "$$",
  };
}

export interface FaqItem {
  q: string;
  a: string;
}

export function faqPageJsonLd(items: FaqItem[]) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((it) => ({
      "@type": "Question",
      name: it.q,
      acceptedAnswer: { "@type": "Answer", text: it.a },
    })),
  };
}

export function websiteJsonLd(locale: Locale) {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "Farveladeland",
    url: locale === "da" ? SITE_URL : `${SITE_URL}/en`,
    inLanguage: locale === "da" ? "da-DK" : "en",
  };
}

export interface ProductLdInput {
  name: string;
  description: string;
  url: string;
  image: string[];
  sku: string;
  price: string;
  currency: string;
  available: boolean;
  brand?: string;
}

export function productJsonLd(p: ProductLdInput) {
  return {
    "@context": "https://schema.org",
    "@type": "Product",
    name: p.name,
    description: p.description,
    image: p.image,
    sku: p.sku,
    brand: { "@type": "Brand", name: p.brand ?? "Farveladeland" },
    offers: {
      "@type": "Offer",
      url: p.url,
      priceCurrency: p.currency,
      price: p.price,
      availability: p.available
        ? "https://schema.org/InStock"
        : "https://schema.org/OutOfStock",
      itemCondition: "https://schema.org/UsedCondition",
    },
  };
}

export interface BreadcrumbItem {
  name: string;
  url: string;
}

export function breadcrumbJsonLd(items: BreadcrumbItem[]) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: it.url,
    })),
  };
}

export function collectionPageJsonLd(opts: {
  name: string;
  description?: string;
  url: string;
  image?: string;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: opts.name,
    description: opts.description,
    url: opts.url,
    ...(opts.image ? { image: opts.image } : {}),
  };
}
